import { useEffect, useState, useRef } from "react";
import { Home, Users, Calendar, User } from "lucide-react";
import apiClient from "../api/apiClient";
import { useJWT } from "../contexts/JWTContext";
import { useBattleTop } from "../hooks/BattleUseTop";
import { useNavigate } from "react-router-dom";
import {
  listTopRatedCoursesForHome,
  listCoursePathPoints,
} from "../api/courseApi";
import { buildGoogleStaticMapUrl } from "../utils/googleStaticMap";
import { Crown } from "lucide-react";

import { useMemo } from "react";

function HomePage() {
  const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_STATIC_MAP_KEY;
  const [currentSlide, setCurrentSlide] = useState(1);
  const [activeTab, setActiveTab] = useState("홈");
  const { userId } = useJWT();
  const [nickname, setNickname] = useState("");

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const slideRef = useRef(null);
  const minSwipeDistance = 50;

  // 코스 추천(상위 5개 + 대표후기 + 후기수)
  const [topRatedCourses, setTopRatedCourses] = useState([]);
  const [topRatedLoading, setTopRatedLoading] = useState(false);
  const [topRatedError, setTopRatedError] = useState("");

  const {
    maleRankers,
    femaleRankers,
    loading: battleTopLoading,
  } = useBattleTop();
  const navigate = useNavigate();

  const maleTop1 = useMemo(() => {
    if (!maleRankers?.length) return null;
    return [...maleRankers].sort((a, b) => b.winCount - a.winCount)[0];
  }, [maleRankers]);

  const femaleTop1 = useMemo(() => {
    if (!femaleRankers?.length) return null;
    return [...femaleRankers].sort((a, b) => b.winCount - a.winCount)[0];
  }, [femaleRankers]);

  // ✅ BattleTopPage와 동일한 이미지 규칙(레벨/아바타)
  const getLevelIconSrc = (runner) =>
    `/images/levelIcon/level_${runner?.level || 1}.png`;

  const getAvatarSrc = (runner) =>
    runner?.avatarUrl
      ? `/images/avatar/${runner.avatarUrl}`
      : "/images/avatar/avatar_basic.png";

  const getDisplayName = (runner) => runner?.nickname || runner?.userId || "—";

  useEffect(() => {
    if (!userId) return;

    const fetchMyInfo = async () => {
      try {
        const res = await apiClient.get(`/user/${userId}`);
        setNickname(res.data.nickname);
      } catch (err) {
        console.error("닉네임 불러오기 실패", err);
      }
    };

    fetchMyInfo();
  }, [userId]);

  useEffect(() => {
    const fetchTopRatedCourses = async () => {
      try {
        setTopRatedLoading(true);
        setTopRatedError("");

        const data = await listTopRatedCoursesForHome();
        setTopRatedCourses(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("인기 코스 불러오기 실패", err);
        setTopRatedError("인기 코스를 불러오지 못했습니다.");
      } finally {
        setTopRatedLoading(false);
      }
    };

    fetchTopRatedCourses();
  }, []);

  // 2. ✅ 추가: 리스트 로드 후 각 코스별 지도 이미지 비동기 로드
  useEffect(() => {
    if (!topRatedCourses.length || !GOOGLE_KEY) return;

    topRatedCourses.forEach(async (item) => {
      // 이미 이미지가 로드된 상태면 건너뜀
      if (item.course.imageUrl) return;

      const courseId = item.course.courseId;
      try {
        const points = await listCoursePathPoints(courseId);
        if (points && points.length > 0) {
          const mapUrl = buildGoogleStaticMapUrl(points, {
            apiKey: GOOGLE_KEY,
            size: "200x200",
            zoom: 13,
          });

          // 해당 코스 객체에만 imageUrl 주입
          setTopRatedCourses((prev) =>
            prev.map((it) =>
              it.course.courseId === courseId
                ? { ...it, course: { ...it.course, imageUrl: mapUrl } }
                : it
            )
          );
        }
      } catch (err) {
        console.error(`지도 데이터 로드 실패(ID: ${courseId}):`, err);
      }
    });
  }, [topRatedCourses.length, GOOGLE_KEY]);

  // 배너 슬라이드 데이터
  const banners = [
    {
      id: 1,
      imageUrl: "/images/banner/banner1.png",
      gradient: "from-black/30 to-black/10",
      link: "/course",
    },
    {
      id: 2,
      imageUrl: "/images/banner/banner2.png",
      gradient: "from-black/30 to-black/10",
      externalLink: "https://seoul-marathon.com/78",
    },
    {
      id: 3,
      imageUrl: "/images/banner/banner3.png",
      gradient: "from-black/30 to-black/10",
    },
  ];

  // 무한 루프를 위한 배너 배열
  const extendedBanners = [banners[banners.length - 1], ...banners, banners[0]];

  // 자동 슬라이드
  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 3000);

    return () => clearInterval(timer);
  }, []);

  // 무한 루프 처리
  useEffect(() => {
    if (!isTransitioning) return;

    if (currentSlide === 0) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentSlide(banners.length);
      }, 500);
      return () => clearTimeout(timer);
    } else if (currentSlide === extendedBanners.length - 1) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentSlide(1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentSlide, banners.length, extendedBanners.length, isTransitioning]);

  // 트랜지션 다시 활성화
  useEffect(() => {
    if (!isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  const nextSlide = () => {
    setIsTransitioning(true);
    setCurrentSlide((prev) => prev + 1);
  };

  const prevSlide = () => {
    setIsTransitioning(true);
    setCurrentSlide((prev) => prev - 1);
  };

  // 터치 스와이프 처리
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // 배너 클릭 핸들러
  const handleBannerClick = (e, banner) => {
    e.preventDefault();

    if (banner.externalLink) {
      window.open(banner.externalLink, "_blank");
    } else if (banner.link) {
      navigate(banner.link);
    }
  };

  // 인디케이터 클릭
  const goToSlide = (index) => {
    setIsTransitioning(true);
    setCurrentSlide(index + 1);
  };

  // 실제 슬라이드 인덱스 (인디케이터용)
  const getRealIndex = () => {
    if (currentSlide === 0) return banners.length - 1;
    if (currentSlide === extendedBanners.length - 1) return 0;
    return currentSlide - 1;
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 헤더 */}
      <header className="bg-white px-5 py-4 flex items-center justify-between shadow-sm">
        <div className="h-[40px] flex items-center overflow-visible">
          <img
            src="/logo.png"
            alt="WINGSTEP"
            className="h-full object-contain scale-75 origin-left translate-y-[1px]"
          />
        </div>

        {/* 닉네임 프로필 칩 */}
        <button
          onClick={() => navigate("/user")}
          className="flex items-center gap-2 bg-gray-100 py-1.5 pl-2 pr-3 rounded-full shadow-sm hover:bg-gray-200 transition-colors"
        >
          <div className="w-7 h-7 bg-teal-300 rounded-full flex items-center justify-center text-white font-bold text-xs shadow">
            {nickname?.charAt(0)?.toUpperCase()}
          </div>
          <span className="text-sm font-medium text-gray-700">{nickname}</span>
        </button>
      </header>

      {/* 메인 배너 슬라이드 */}
      <section
        className="relative mx-5 mt-5 mb-6 overflow-hidden rounded-3xl shadow-xl"
        style={{ height: "220px" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          ref={slideRef}
          className="flex h-full"
          style={{
            transform: `translateX(-${currentSlide * 100}%)`,
            transition: isTransitioning ? "transform 500ms ease-out" : "none",
          }}
        >
          {extendedBanners.map((banner, index) => (
            <button
              key={`${banner.id}-${index}`}
              className={`min-w-full h-full relative focus:outline-none ${
                banner.link || banner.externalLink
                  ? "cursor-pointer"
                  : "cursor-default"
              }`}
              onClick={(e) => handleBannerClick(e, banner)}
            >
              {banner.imageUrl ? (
                // 이미지가 있는 경우
                <div className="w-full h-full relative">
                  <img
                    src={banner.imageUrl}
                    alt={banner.title || "배너"}
                    className="w-full h-full object-cover"
                  />
                  {/* 이미지 위 오버레이 */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
                  {/* 텍스트 */}
                  {banner.title && (
                    <div className="absolute bottom-6 left-6 right-6">
                      <h3 className="text-2xl font-black text-white drop-shadow-lg">
                        {banner.title}
                      </h3>
                    </div>
                  )}
                </div>
              ) : (
                // 그라데이션 배경만 (이미지 없는 경우)
                <div
                  className={`w-full h-full bg-gradient-to-br ${banner.gradient} flex flex-col items-center justify-center text-white p-6`}
                >
                  {banner.emoji && (
                    <div className="text-6xl mb-3">{banner.emoji}</div>
                  )}
                  {banner.title && (
                    <h3 className="text-2xl font-black text-center">
                      {banner.title}
                    </h3>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* 슬라이드 인디케이터 */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-1.5 z-10">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-1.5 rounded-full transition-all ${
                getRealIndex() === index ? "bg-white w-6" : "bg-white/50 w-1.5"
              }`}
            />
          ))}
        </div>
      </section>

      {/* 대결 카드 - 세련된 스타일 */}
      <section className="px-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">RANK</h2>
          <button
            onClick={() => navigate("/battle/top")}
            className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
          >
            더보기
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {/* 남자 1등 */}
          <button
            onClick={() => navigate("/battle/top")}
            className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-all active:scale-98"
          >
            <div className="flex flex-col items-center gap-3">
              {/* 라벨 */}
              <div className="w-full flex items-center justify-between">
                <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-lg">
                  MALE
                </span>
                <Crown className="w-4 h-4 text-yellow-500" />
              </div>

              {/* 아바타 */}
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-gray-100 overflow-hidden border-2 border-gray-200">
                  {battleTopLoading ? (
                    <div className="w-full h-full animate-pulse bg-gray-200" />
                  ) : (
                    <img
                      src={getAvatarSrc(maleTop1)}
                      alt="avatar"
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                {/* 레벨 뱃지 */}
                <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-200">
                  <img
                    src={getLevelIconSrc(maleTop1)}
                    alt="level"
                    className="w-6 h-6 object-contain"
                  />
                </div>
              </div>

              {/* 닉네임 */}
              <p className="text-sm font-semibold text-gray-900 truncate w-full text-center">
                {battleTopLoading ? "..." : getDisplayName(maleTop1)}
              </p>
            </div>
          </button>

          {/* 여자 1등 */}
          <button
            onClick={() => navigate("/battle/top")}
            className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-all active:scale-98"
          >
            <div className="flex flex-col items-center gap-3">
              {/* 라벨 */}
              <div className="w-full flex items-center justify-between">
                <span className="text-xs font-semibold text-pink-600 bg-pink-50 px-2 py-1 rounded-lg">
                  FEMALE
                </span>
                <Crown className="w-4 h-4 text-yellow-500" />
              </div>

              {/* 아바타 */}
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-gray-100 overflow-hidden border-2 border-gray-200">
                  {battleTopLoading ? (
                    <div className="w-full h-full animate-pulse bg-gray-200" />
                  ) : (
                    <img
                      src={getAvatarSrc(femaleTop1)}
                      alt="avatar"
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                {/* 레벨 뱃지 */}
                <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-200">
                  <img
                    src={getLevelIconSrc(femaleTop1)}
                    alt="level"
                    className="w-6 h-6 object-contain"
                  />
                </div>
              </div>

              {/* 닉네임 */}
              <p className="text-sm font-semibold text-gray-900 truncate w-full text-center">
                {battleTopLoading ? "..." : getDisplayName(femaleTop1)}
              </p>
            </div>
          </button>
        </div>
      </section>

      {/* 코스 추천 - 베스트 5 */}
      <section className="px-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Best Courses</h2>
          <button
            onClick={() => navigate("/course")}
            className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
          >
            전체보기
          </button>
        </div>

        {/* 로딩/에러/없음 상태 생략... */}

        <div className="space-y-3">
          {topRatedCourses.slice(0, 5).map((item, index) => {
            const course = item.course;
            const repReview = item.representativeReview;
            const reviewCount = item.reviewCount || 0;

            // ✅ 상태 업데이트로 들어온 지도 URL 사용
            const thumbSrc = course.imageUrl;

            return (
              <button
                key={course.courseId}
                className="w-full bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-all active:scale-[0.99] text-left"
                onClick={() => navigate(`/course/${course.courseId}`)}
              >
                <div className="flex gap-4 p-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <span className="text-sm font-bold text-gray-700">
                      {index + 1}
                    </span>
                  </div>

                  {/* 썸네일 영역 - 지도 표시 */}
                  <div className="w-20 h-20 flex-shrink-0 rounded-xl bg-gray-50 overflow-hidden border border-gray-100 relative">
                    {thumbSrc ? (
                      <img
                        src={thumbSrc}
                        alt={course.courseName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    {/* 로딩 중이거나 지도 데이터가 없을 때의 기본 배경 */}
                    <div
                      className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50"
                      style={{ display: thumbSrc ? "none" : "flex" }}
                    >
                      <span className="text-2xl animate-pulse">🏃</span>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
                    <h3 className="text-sm font-bold text-gray-900 truncate">
                      {course.courseName}
                    </h3>
                    <div className="flex items-center gap-2">
                      {course.avgRating > 0 ? (
                        <div className="flex items-center gap-1 bg-yellow-50 px-1.5 py-0.5 rounded text-xs">
                          <span className="text-yellow-500">★</span>
                          <span className="font-bold text-gray-800">
                            {Number(course.avgRating).toFixed(1)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
                          평가 없음
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        리뷰 {reviewCount > 99 ? "99+" : reviewCount}
                      </span>
                    </div>
                    {repReview?.reviewContent ? (
                      <p className="text-xs text-gray-500 line-clamp-1 mt-1 bg-gray-50 px-2 rounded-lg w-fit max-w-full">
                        "{repReview.reviewContent}"
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400 mt-1 pl-1">
                        아직 작성된 후기가 없어요
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* 하단 네비게이션 바 */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-5 py-3">
        <div className="flex justify-around items-center">
          <button
            onClick={() => setActiveTab("홈")}
            className={`flex flex-col items-center gap-1 ${
              activeTab === "홈" ? "text-[rgb(0,224,198)]" : "text-gray-400"
            }`}
          >
            <Home className="w-6 h-6" />
            <span className="text-xs font-medium">홈</span>
          </button>

          <button
            onClick={() => setActiveTab("코스")}
            className={`flex flex-col items-center gap-1 ${
              activeTab === "코스" ? "text-[rgb(0,224,198)]" : "text-gray-400"
            }`}
          >
            <Users className="w-6 h-6" />
            <span className="text-xs font-medium">코스</span>
          </button>

          <button
            onClick={() => setActiveTab("대결")}
            className={`flex flex-col items-center gap-1 ${
              activeTab === "대결" ? "text-[rgb(0,224,198)]" : "text-gray-400"
            }`}
          >
            <Calendar className="w-6 h-6" />
            <span className="text-xs font-medium">대결</span>
          </button>

          <button
            onClick={() => setActiveTab("마이")}
            className={`flex flex-col items-center gap-1 ${
              activeTab === "마이" ? "text-[rgb(0,224,198)]" : "text-gray-400"
            }`}
          >
            <User className="w-6 h-6" />
            <span className="text-xs font-medium">마이</span>
          </button>
        </div>
      </nav>
    </div>
  );
}

export default HomePage;
